<template>
  <v-card class="elevation-0 mt-9">
    <v-card-text class="pa-0">
      <v-row>
        <v-col cols="12" md="8" lg="6" class="mx-3">
          <!-- Logo Upload Section -->
          <div class="mb-6">
            <h3 class="text-h6 mb-4">
              <v-icon left small>mdi-image</v-icon>
              {{ $t('setting.sections.company_logo') || 'Company Logo' }}
            </h3>

            <div class="logo-upload-area">
              <!-- Current Logo Display -->
              <div v-if="currentLogo" class="current-logo mb-4">
                <p class="text-subtitle-2 mb-2">{{ $t('setting.logo.current') || 'Current Logo' }}</p>
                <v-card outlined class="pa-4" max-width="300">
                  <v-img
                    :src="currentLogo"
                    max-height="150"
                    contain
                    class="mb-3"
                  />
                  <v-btn
                    color="error"
                    small
                    block
                    outlined
                    @click="removeLogo"
                    :loading="removing"
                  >
                    <v-icon left small>mdi-delete</v-icon>
                    {{ $t('button.remove') || 'Remove Logo' }}
                  </v-btn>
                </v-card>
              </div>

              <!-- Upload New Logo -->
              <div class="upload-section">
                <p class="text-subtitle-2 mb-2">
                  {{ currentLogo ? $t('setting.logo.replace') : $t('setting.logo.upload') || 'Upload Logo' }}
                </p>

                <v-file-input
                  v-model="logoFile"
                  :label="$t('setting.logo.select_file') || 'Select logo file'"
                  prepend-icon="mdi-image"
                  outlined
                  dense
                  accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                  :rules="fileRules"
                  show-size
                  @change="onFileChange"
                >
                  <template v-slot:selection="{ text }">
                    <v-chip
                      small
                      label
                      color="primary"
                    >
                      {{ text }}
                    </v-chip>
                  </template>
                </v-file-input>

                <!-- Preview -->
                <v-expand-transition>
                  <v-card
                    v-if="preview"
                    outlined
                    class="pa-4 mb-4"
                    max-width="300"
                  >
                    <p class="text-caption mb-2">{{ $t('setting.logo.preview') || 'Preview' }}</p>
                    <v-img
                      :src="preview"
                      max-height="150"
                      contain
                      class="mb-3"
                    />
                  </v-card>
                </v-expand-transition>

                <!-- Upload Button -->
                <v-btn
                  color="primary"
                  :disabled="!logoFile || loading"
                  :loading="loading"
                  @click="uploadLogo"
                  v-can.disable="'setting.logo'"
                >
                  <v-icon left>mdi-upload</v-icon>
                  {{ $t('button.upload') || 'Upload Logo' }}
                </v-btn>
              </div>
            </div>

            <!-- Logo Guidelines -->
            <v-alert
              type="info"
              text
              dense
              border="left"
              class="mt-4"
            >
              <div class="text-subtitle-2 font-weight-medium mb-2">
                {{ $t('setting.logo.guidelines_title') || 'Logo Guidelines' }}
              </div>
              <ul class="text-caption pl-4 mb-0">
                <li>{{ $t('setting.logo.guideline1') || 'Recommended size: 300x100 pixels' }}</li>
                <li>{{ $t('setting.logo.guideline2') || 'Supported formats: PNG, JPG, JPEG, SVG' }}</li>
                <li>{{ $t('setting.logo.guideline3') || 'Maximum file size: 2MB' }}</li>
                <li>{{ $t('setting.logo.guideline4') || 'Transparent background recommended for best results' }}</li>
              </ul>
            </v-alert>
          </div>
        </v-col>

        <!-- Help Section -->
        <v-col cols="12" md="4" lg="6" class="pl-md-6">
          <v-alert
            type="warning"
            text
            dense
            border="left"
            class="mb-4"
          >
            <div class="text-subtitle-2 font-weight-medium mb-2">
              {{ $t('setting.logo.usage_title') || 'Logo Usage' }}
            </div>
            <div class="text-caption">
              {{ $t('setting.logo.usage_desc') || 'Your company logo will appear on:' }}
            </div>
            <ul class="text-caption mt-2 pl-4">
              <li>{{ $t('setting.logo.usage1') || 'Invoices and receipts' }}</li>
              <li>{{ $t('setting.logo.usage2') || 'Reports and documents' }}</li>
              <li>{{ $t('setting.logo.usage3') || 'Email communications' }}</li>
              <li>{{ $t('setting.logo.usage4') || 'Customer portal' }}</li>
            </ul>
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      :color="snackbarColor"
      bottom
      right
    >
      <v-icon left>{{ snackbarIcon }}</v-icon>
      {{ snackbarText }}
    </v-snackbar>
  </v-card>
</template>

<script>
export default {
  name: 'Logo',
  data () {
    return {
      currentLogo: null,
      logoFile: null,
      preview: null,
      loading: false,
      removing: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      snackbarIcon: 'mdi-check-circle',
      fileRules: [
        v => !v || v.size < 2000000 || 'Logo size should be less than 2 MB!'
      ]
    }
  },
  methods: {
    onFileChange (file) {
      if (!file) {
        this.preview = null
        return
      }

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        this.preview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    async uploadLogo () {
      if (!this.logoFile) return

      this.loading = true
      try {
        // Create FormData for file upload
        const formData = new FormData()
        formData.append('logo', this.logoFile)

        // Here you would call your API to upload the logo
        await this.$configService.uploadLogo(formData)

        this.currentLogo = this.preview
        this.logoFile = null
        this.preview = null

        this.showSnackbar(this.$t('setting.logo.upload_success') || 'Logo uploaded successfully')
      } catch (error) {
        this.globalErrHandle(error)
        this.showSnackbar(this.$t('setting.logo.upload_error') || 'Failed to upload logo', 'error')
      } finally {
        this.loading = false
      }
    },
    async removeLogo () {
      this.removing = true
      try {
        await this.$configService.removeLogo()

        this.currentLogo = null
        this.showSnackbar(this.$t('setting.logo.remove_success') || 'Logo removed successfully')
      } catch (error) {
        this.globalErrHandle(error)
        this.showSnackbar(this.$t('setting.logo.remove_error') || 'Failed to remove logo', 'error')
      } finally {
        this.removing = false
      }
    },
    showSnackbar (text, type = 'success') {
      this.snackbarText = text
      this.snackbarColor = type
      this.snackbarIcon = type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'
      this.snackbar = true
    },
    async loadCurrentLogo () {
      try {
        const config = await this.$configService.getConfig()
        if (config.company_logo) {
          this.currentLogo = config.company_logo
        }
      } catch (error) {
        console.error('Failed to load logo:', error)
      }
    }
  },
  mounted () {
    // Emit title and description to parent
    this.$emit('parent-title', this.$t('setting.tabs.logo') || 'Company Logo')
    this.$emit('parent-description', this.$t('setting.desc.logo_full') || 'Upload and manage your company logo for use across the system')

    // Load current logo
    this.loadCurrentLogo()
  }
}
</script>

<style scoped>
/* Remove default card shadow for seamless integration */
.v-card {
  background: transparent;
}

/* Ensure proper spacing on mobile */
@media (max-width: 959px) {
  .pl-md-6 {
    padding-left: 0 !important;
    padding-top: 24px;
  }
}

.current-logo .v-card {
  background: white;
}

.theme--dark .current-logo .v-card {
  background: #1e1e1e;
}
</style>
